import { isData, isMenuToggleMessage, listen } from "@sinch/bus";
import type { FC } from "react";
import { createContext, useContext, useEffect, useReducer } from "react";
import {
  getLocalStorage,
  IS_SIDEBAR_COLLAPSED,
  setLocalStorage,
} from "../helpers/localStorage";

export type SidebarState = {
  /** The menu is collapsed to the side. */
  isCollapsed: boolean;
  openedSubMenu: string | undefined;
};

const initialState = (): SidebarState => ({
  isCollapsed: getLocalStorage(
    IS_SIDEBAR_COLLAPSED,
    (x): x is boolean => typeof x === "boolean",
    false
  ),
  openedSubMenu: undefined,
});

const SidebarContext = createContext<{
  state: SidebarState;
  dispatch: (x: { type: Actions; payload?: { subMenuId?: string } }) => void;
} | null>(null);

export const SidebarContextProvider: FC = ({ children }) => {
  const [state, dispatch] = useReducer(hoverReducer, initialState());

  return (
    <SidebarContext.Provider value={{ state, dispatch }}>
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebarState = (): SidebarState => {
  const context = useContext(SidebarContext);
  if (context === null)
    throw Error(
      "Cannot use useSidebarState without SidebarContextProvider in the parent tree."
    );

  return {
    ...context.state,
    isCollapsed: context.state.isCollapsed,
  };
};

export const useSidebarDispatch = () => {
  const context = useContext(SidebarContext);
  if (context === null)
    throw Error(
      "Cannot use useSidebarDispatch without SidebarContextProvider in the parent tree."
    );
  return context.dispatch;
};

export const useSidebarBusEventHandler = () => {
  const dispatch = useSidebarDispatch();

  useEffect(() => {
    listen((message) => {
      if (isData(message)) {
        dispatch({ type: message.payload });
      }
    }, isMenuToggleMessage);
  }, [dispatch]);
};

export type Actions =
  | "COLLAPSE"
  | "EXPAND"
  | "SUBMENU_CLICK"
  | "SUBMENU_ARROW_CLICKED";

const hoverReducer = (
  state: SidebarState,
  { type, payload }: { type: Actions; payload?: { subMenuId?: string } }
): SidebarState => {
  switch (type) {
    case "COLLAPSE":
      setLocalStorage("shell:is-sidebar-collapsed", true);
      return { ...state, isCollapsed: true };
    case "EXPAND":
      setLocalStorage("shell:is-sidebar-collapsed", false);
      return { ...state, isCollapsed: false };
    case "SUBMENU_CLICK":
      return { ...state, openedSubMenu: payload?.subMenuId };
    case "SUBMENU_ARROW_CLICKED":
      if (payload?.subMenuId === state.openedSubMenu) {
        return { ...state, openedSubMenu: undefined };
      }
      return { ...state, openedSubMenu: payload?.subMenuId };
  }
};
